import { Link } from "@frontend/lib/link";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { formatDateTime } from "@creators/backend/lib/time";
import { clientApi } from "@frontend/lib/api";
import { useAsync } from "@frontend/lib/use-async";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { branding } from "@creators/backend/lib/config";

type ContractRow = {
  terms: { acceptedBy: string; acceptedAt: string | Date };
  collab: { id: string; reference: string };
  html: string;
};

function downloadAgreement(reference: string, html: string) {
  const doc = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>Agreement ${reference}</title>
<style>body{font-family:system-ui,sans-serif;max-width:40rem;margin:2rem auto;padding:0 1.25rem;line-height:1.65;color:#111}a{word-break:break-all;color:#111}h2{font-size:0.75rem;letter-spacing:0.06em;text-transform:uppercase;color:#555;margin:1.75rem 0 0.5rem}table{width:100%;border-collapse:collapse;font-size:0.9rem}th,td{text-align:left;padding:0.5rem 0.35rem;border-bottom:1px solid #ddd}</style>
</head><body>${html}</body></html>`;
  const blob = new Blob([doc], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `agreement-${reference.replace(/[^\w.-]+/g, "_")}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ContractPage() {
  const { id = "" } = useParams<{ id: string }>();
  useEffect(() => { document.title = `Agreement · ${branding.name}`; }, []);

  const state = useAsync(
    async () => {
      if (!/^[0-9a-f-]{36}$/i.test(id)) return null;
      return clientApi<ContractRow>(`/api/collabs/${id}/contract`).catch(() => null);
    },
    [id],
    { cacheKey: /^[0-9a-f-]{36}$/i.test(id) ? `contract:${id}` : null },
  );

  if (state.status === "loading" && !state.data) {
    return <PageSkeleton />;
  }
  if (!state.data) return <Navigate to={`/collabs/${id}`} replace />;
  const row = state.data;
  const acceptedBy = row.terms.acceptedBy === "brand" ? "Collaborator" : "Creator";

  return (
    <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-10">
      <div className="flex items-center justify-between gap-3">
        <Link href={`/collabs/${id}`} className="text-sm text-ink-muted hover:text-ink">
          ← Back to the collab
        </Link>
        <button
          type="button"
          onClick={() => downloadAgreement(row.collab.reference, row.html)}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-muted transition hover:bg-canvas hover:text-ink"
        >
          <DownloadIcon />
          Download
        </button>
      </div>

      <article
        className="mt-5 border border-line bg-surface px-7 py-9 leading-relaxed text-ink shadow-sm shadow-ink/[0.03]
          [&_header]:mb-6 [&_header]:border-b [&_header]:border-line [&_header]:pb-4
          [&_h1]:text-[1.35rem] [&_h1]:font-semibold [&_h1]:tracking-tight
          [&_.meta]:mt-1.5 [&_.meta]:text-[13px] [&_.meta]:text-ink-muted
          [&_h2]:mt-7 [&_h2]:text-[11px] [&_h2]:font-semibold [&_h2]:tracking-[0.08em] [&_h2]:text-ink-muted [&_h2]:uppercase
          [&_p]:mt-2.5 [&_p]:text-[15px] [&_p]:leading-[1.65]
          [&_a]:break-all [&_a]:underline [&_a]:decoration-line/60 [&_a]:underline-offset-2
          [&_table]:mt-4 [&_table]:w-full [&_table]:text-sm
          [&_th]:border-b [&_th]:border-line [&_th]:pb-2 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-semibold [&_th]:tracking-[0.06em] [&_th]:text-ink-muted [&_th]:uppercase
          [&_td]:border-b [&_td]:border-line/80 [&_td]:py-2.5"
        dangerouslySetInnerHTML={{ __html: row.html }}
      />

      <p className="mt-6 text-xs text-ink-subtle">
        Accepted by the {acceptedBy} on {formatDateTime(row.terms.acceptedAt)}.
      </p>
    </main>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
      <path
        d="M8 2.5v7m0 0L5.5 7M8 9.5 10.5 7M3 12.5h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
